import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

export function DeleteAccountZone() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const del = async () => {
    setBusy(true);
    try {
      await api.delete("/account");
      toast.success("Your account has been deleted");
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-12 border border-red-200 rounded-sm p-6 bg-red-50/40" data-testid="danger-zone">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-red-600" />
        <h3 className="font-display font-semibold text-red-700">Danger zone</h3>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Permanently delete your account, profile, offers and messages. This cannot be undone.
      </p>
      <Button
        variant="outline"
        className="border-red-300 text-red-600 hover:bg-red-100 hover:text-red-700"
        data-testid="delete-account-btn"
        onClick={() => setOpen(true)}
      >
        Delete account
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete your account?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This permanently removes your account and all associated data (profile, offers, messages).
            This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-account"
              disabled={busy}
              onClick={del}
            >
              {busy ? "Deleting…" : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
